import React, { useState, useEffect } from 'react';

const DEFAULT_WAN = { interface: 'ether1', gateway: '', ping_target: '8.8.8.8' };
const DEFAULTS = { method: 'pcc', check_gateway: true, recursive_failover: true, ping_interval: '10s', ping_timeout: '5s', ping_failure_count: 3, wan_interfaces: [{ ...DEFAULT_WAN }] };

export default function StepLoadBalance({ data, onChange, onNext, onBack, interfaceNames }) {
  const [form, setForm] = useState(data || DEFAULTS);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const update = (key, value) => {
    const updated = { ...form, [key]: value };
    setForm(updated);
    onChange(updated);
  };

  const updateWan = (index, key, value) => {
    const updated = [...form.wan_interfaces];
    updated[index] = { ...updated[index], [key]: value };
    update('wan_interfaces', updated);
  };

  const addWan = () => {
    update('wan_interfaces', [...form.wan_interfaces, { ...DEFAULT_WAN, interface: 'ether' + (form.wan_interfaces.length + 1) }]);
  };

  const removeWan = (index) => {
    if (form.wan_interfaces.length <= 1) return;
    update('wan_interfaces', form.wan_interfaces.filter((_, i) => i !== index));
  };

  const methods = [
    { value: 'pcc', label: 'PCC', desc: 'กระจายตาม IP/พอร์ต เหมาะกับหลาย WAN' },
    { value: 'ecmp', label: 'ECMP', desc: 'กระจายเท่าๆ กัน ทุกเส้นใช้พร้อมกัน' },
    { value: 'failover', label: 'Failover', desc: 'ใช้เส้นหลัก เส้นสำรองรอเมื่อเสีย' },
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">⚖️ โหลดบาลานซ์ / Failover</h2>
        <p className="text-gray-500 dark:text-gray-400">ใช้หลาย WAN พร้อมกันเพื่อกระจายทราฟฟิก หรือสำรองเมื่อสายหลักเสีย</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="label">โหมดการทำงาน</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {methods.map(m => (
              <button
                key={m.value}
                onClick={() => update('method', m.value)}
                className={`p-3 rounded-xl border-2 text-left text-sm transition-all ${
                  form.method === m.value
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 ring-1 ring-teal-200 dark:ring-teal-700'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400'
                }`}
              >
                <div className="font-bold">{m.label}</div>
                <div className="text-xs opacity-70 mt-0.5">{m.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Recursive Failover */}
        <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/10 dark:to-orange-900/10 border border-amber-200 dark:border-amber-800/50 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-bold text-amber-800 dark:text-amber-300 flex items-center gap-2">
                <span className="text-lg">🔄</span> Recursive Failover (เช็คอินเทอร์เน็ตจริง)
              </h3>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">ping 8.8.8.8 ผ่านแต่ละ WAN เพื่อเช็คเน็ตออกนอกได้จริง ไม่ใช่แค่ Link Up/Down</p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">เปิดใช้งาน</span>
              <input type="checkbox" checked={form.recursive_failover} onChange={e => update('recursive_failover', e.target.checked)} className="w-5 h-5 text-amber-600 rounded" />
            </label>
          </div>

          {form.recursive_failover && (
            <div className="space-y-3 mt-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="label text-xs">ความถี่ Ping</label>
                  <select value={form.ping_interval} onChange={e => update('ping_interval', e.target.value)} className="input-field text-sm">
                    <option value="5s">ทุก 5 วินาที</option>
                    <option value="10s">ทุก 10 วินาที</option>
                    <option value="15s">ทุก 15 วินาที</option>
                    <option value="30s">ทุก 30 วินาที</option>
                    <option value="1m">ทุก 1 นาที</option>
                  </select>
                </div>
                <div>
                  <label className="label text-xs">Timeout</label>
                  <select value={form.ping_timeout} onChange={e => update('ping_timeout', e.target.value)} className="input-field text-sm">
                    <option value="3s">3 วินาที</option>
                    <option value="5s">5 วินาที</option>
                    <option value="10s">10 วินาที</option>
                  </select>
                </div>
                <div>
                  <label className="label text-xs">จำนวนครั้งก่อนสลับ</label>
                  <select value={form.ping_failure_count} onChange={e => update('ping_failure_count', parseInt(e.target.value))} className="input-field text-sm">
                    <option value={2}>2 ครั้ง</option>
                    <option value={3}>3 ครั้ง</option>
                    <option value={5}>5 ครั้ง</option>
                  </select>
                </div>
              </div>
              <div className="p-2 bg-amber-100 dark:bg-amber-900/20 rounded-lg">
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  <strong>💡 วิธีทำงาน:</strong> Router จะ ping {form.wan_interfaces[0]?.ping_target || '8.8.8.8'} ผ่านแต่ละ WAN ทุก {form.ping_interval} ถ้าไม่ตอบ {form.ping_failure_count} ครั้ง จะสลับไป WAN ถัดไปอัตโนมัติ
                </p>
              </div>
            </div>
          )}
        </div>

        <label className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 cursor-pointer">
          <div>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">ตรวจสอบ Gateway (Ping)</span>
            <p className="text-xs text-gray-400 mt-0.5">สลับ WAN อัตโนมัติเมื่อตรวจสอบว่าเสีย</p>
          </div>
          <input type="checkbox" checked={form.check_gateway} onChange={e => update('check_gateway', e.target.checked)} className="w-5 h-5 text-teal-600 rounded" />
        </label>

        <div>
          <label className="label">รายการ WAN</label>
          {form.wan_interfaces.map((wan, index) => (
            <div key={index} className="flex flex-col sm:flex-row gap-2 mb-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-2 sm:w-auto">
                <span className="w-6 h-6 bg-teal-500 text-white rounded flex items-center justify-center text-xs font-bold flex-shrink-0">{index + 1}</span>
              </div>
              <select value={wan.interface} onChange={e => updateWan(index, 'interface', e.target.value)} className="input-field text-sm sm:w-32">
                {(interfaceNames && interfaceNames.length > 0 ? interfaceNames : ['ether1', 'ether2', 'ether3', 'ether4', 'sfp-sfpplus1']).map(iface => (
                  <option key={iface} value={iface}>{iface}</option>
                ))}
              </select>
              <input type="text" value={wan.gateway} onChange={e => updateWan(index, 'gateway', e.target.value)} className="input-field text-sm flex-1" placeholder="Gateway IP" />
              {form.recursive_failover && (
                <input type="text" value={wan.ping_target || '8.8.8.8'} onChange={e => updateWan(index, 'ping_target', e.target.value)} className="input-field text-sm sm:w-32" placeholder="Ping Target (8.8.8.8)" />
              )}
              <button onClick={() => removeWan(index)} className="btn-danger" disabled={form.wan_interfaces.length <= 1}>×</button>
            </div>
          ))}
          <button onClick={addWan} className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 font-medium mt-1">+ เพิ่ม WAN</button>
        </div>
      </div>

      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="btn-secondary">← ย้อนกลับ</button>
        <button onClick={onNext} className="btn-primary">ถัดไป ({form.wan_interfaces.length} WAN)</button>
      </div>
    </div>
  );
}
